import * as APIUtils from '../components/Globals/Utils';

/**
 * Types for CustomFieldsService
 */
interface BaseRequestConfig {
  api: APIUtils.ApiConfig;
}

interface SaveRequestConfig extends BaseRequestConfig {
  request: any; // Puedes tipar esto mejor según tu API
}

interface GetCustomFieldsByItemIdConfig extends BaseRequestConfig {
  coreTaskId: string | number;
  isDeleted: boolean;
  projectId: string | number;
  isAllPhaseResume?: boolean;
}

interface GetEmptyCustomFieldsConfig extends BaseRequestConfig {
  configValueId?: string | number;
  projectId?: string | number;
}

interface GetListComboBoxConfig extends BaseRequestConfig {
  serviceUrl: string;
}

interface GetImageConfig extends BaseRequestConfig {
  request: string | number;
}

interface GetPersonCustomFieldsConfig extends BaseRequestConfig {
  personId: string | number;
  isDeleted: boolean;
}

interface GetProjectCustomFieldsConfig extends BaseRequestConfig {
  projectId: string | number;
  isDeleted: boolean;
}

interface GetLayersConfig extends BaseRequestConfig {
  projectId: string | number;
  configValueId: string | number;
}
interface GetMergedCustomFieldsByIdsConfig extends BaseRequestConfig {
  conversationIds: string | number;
  coreTaskIds: string | number;
}

/**
 * Service for managing calls to custom fields endpoints
 */
class CustomFieldsService
{
  static save<T = any>(requestConfig: SaveRequestConfig): Promise<T>
  {
    return APIUtils.callFetch<T>(
      requestConfig.api,
      'api/customfieldvalue/',
      'PUT',
      requestConfig.request,
      true,
    );
  }

  static savePost<T = any>(requestConfig: SaveRequestConfig): Promise<T>
  {
    return APIUtils.callFetch<T>(
      requestConfig.api,
      'api/customfieldvalue/',
      'POST',
      requestConfig.request,
    );
  }

  static getCustomFieldsByItemId<T = any>(
    requestConfig: GetCustomFieldsByItemIdConfig,
  ): Promise<T>
  {
    const params = new URLSearchParams({
      CoreTaskId: String(requestConfig.coreTaskId),
      IsDeleted: String(requestConfig.isDeleted),
      ProjectId: String(requestConfig.projectId),
    });
    if (requestConfig.isAllPhaseResume !== undefined)
    {
      params.set('IsAllPhaseResume', 'undefined');
    }

    return APIUtils.callFetch<T>(
      requestConfig.api,
      `api/CustomField/GetCustomFieldsByItemId?${params.toString()}`,
    );
  }

  static getEmptyCustomFieldsByConfigValueId<T = any>(
    requestConfig: GetEmptyCustomFieldsConfig,
  ): Promise<T>
  {
    const params = new URLSearchParams();

    if (requestConfig.configValueId)
    {
      params.append('configValueId', String(requestConfig.configValueId));
    }
    if (requestConfig.projectId)
    {
      params.append('ProjectId', String(requestConfig.projectId));
    }

    const queryString = params.toString();
    const url = queryString
      ? `api/configvalue/GetEmptyCustomFieldsByConfigValueId?${queryString}`
      : 'api/configvalue/GetEmptyCustomFieldsByConfigValueId';

    return APIUtils.callFetch<T>(requestConfig.api, url);
  }

  static getListComboBox<T = any>(requestConfig: GetListComboBoxConfig): Promise<T>
  {
    return APIUtils.callFetch<T>(requestConfig.api, requestConfig.serviceUrl);
  }

  static getImageResult<T = any>(requestConfig: GetImageConfig): Promise<T>
  {
    const params = new URLSearchParams({
      customImageId: String(requestConfig.request),
    });

    return APIUtils.callFetch<T>(
      requestConfig.api,
      `api/customfieldvalue/GetImageResult?${params.toString()}`,
    );
  }

  static getThumbnail<T = any>(requestConfig: GetImageConfig): Promise<T>
  {
    const params = new URLSearchParams({
      customValue: String(requestConfig.request),
    });

    return APIUtils.callFetch<T>(
      requestConfig.api,
      `api/customfieldvalue/GetThumbnail?${params.toString()}`,
    );
  }

  static uploadImage<T = any>(requestConfig: SaveRequestConfig): Promise<T>
  {
    return APIUtils.callFetch<T>(
      requestConfig.api,
      'api/uploadimage/',
      'POST',
      requestConfig.request,
    );
  }

  static getPersonCustomFieldsByItemId<T = any>(
    requestConfig: GetPersonCustomFieldsConfig,
  ): Promise<T>
  {
    const params = new URLSearchParams({
      PersonId: String(requestConfig.personId),
      IsDeleted: String(requestConfig.isDeleted),
    });

    return APIUtils.callFetch<T>(
      requestConfig.api,
      `api/CustomField/GetPersonCustomFieldsByItemId?${params.toString()}`,
    );
  }

  static getProjectCustomFieldsByItemId<T = any>(
    requestConfig: GetProjectCustomFieldsConfig,
  ): Promise<T>
  {
    const params = new URLSearchParams({
      ProjectId: String(requestConfig.projectId),
      IsDeleted: String(requestConfig.isDeleted),
      IsProject: 'true',
      GetGroupsOneCall: 'false',
    });

    return APIUtils.callFetch<T>(
      requestConfig.api,
      `api/CustomField/GetProjectCustomFieldsByItemId?${params.toString()}`,
    );
  }

  static getLayersByConfigValueId<T = any>(requestConfig: GetLayersConfig): Promise<T>
  {
    const params = new URLSearchParams({
      ProjectId: String(requestConfig.projectId),
      ConfigValueId: String(requestConfig.configValueId),
    });

    return APIUtils.callFetch<T>(
      requestConfig.api,
      `api/layer/GetLayersByConfigValueId?${params.toString()}`,
    );
  }

  static GetMergedCustomFieldsByIds<T = any>(
    requestConfig: GetMergedCustomFieldsByIdsConfig,
  ): Promise<T>
  {
    const { conversationIds, coreTaskIds } = requestConfig;

    let urlParams = `?ConversationIds=${conversationIds}`;
    if (coreTaskIds && coreTaskIds != '')
    {
      urlParams += `&CoreTaskIds=${coreTaskIds}`;
    }
    return APIUtils.callFetch<T>(
      requestConfig.api,
      `api/CustomField/GetMergedCustomFieldsByIds${urlParams.toString()}`,
    );
  }
}

export default CustomFieldsService;
